import { CommonModule, Location, LocationStrategy, PathLocationStrategy } from "@angular/common";
import { APP_INITIALIZER, Injector, ModuleWithProviders, NgModule } from "@angular/core";
import { RouteReuseStrategy, RouterModule as NgRouterModule } from "@angular/router";
import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import { CacheFactory, GlobalModule, PlatformService, WindowRef } from "@obg/ngx.global";
import { isFunction } from "lodash-es";

import { ObgRouteReuseStrategy } from "./config/route-reuse-strategy";
import { _ROUTER_CONFIG, ROUTER_CONFIG, RouterConfig } from "./config/router-config.model";
import { ExternalApiEffect } from "./external-api/external-api.effect";
import { LazyLoadingService } from "./lazy-loading/lazy-loading.service";
import { LinkHandlerService } from "./link-handler/link-handler.service";
import { LOCALIZE_ROUTES_PROVIDER, REMOVE_DISABLED_ROUTES_PROVIDER } from "./route-hooks";
import { RouteLinkDirective } from "./route-link/route-link.directive";
import { RouteLinkActiveDirective } from "./route-link-active/route-link-active.directive";
import { AgressiveHistoryService, HistoryService } from "./store/history.service";
import { RouteEffect } from "./store/route.effect";
import { RouteService } from "./store/route.service";

const ROUTING_UI_DIRECTIVES: any[] = [RouteLinkActiveDirective, RouteLinkDirective];

@NgModule({
	imports: [
		CommonModule,
		NgRouterModule,
		StoreModule,
		GlobalModule,
		EffectsModule.forFeature([RouteEffect, ExternalApiEffect]),
	],
	exports: [ROUTING_UI_DIRECTIVES, NgRouterModule],
	declarations: [ROUTING_UI_DIRECTIVES],
})
export class RouterModule {
	/**
	 * Call `RouterModule.forRoot` in your application module.
	 *
	 * @param routerConfig RouterConfig is required, specify it in `forRoot` or alternatively using `ROUTER_CONFIG` token to utilize DI.
	 */
	static forRoot(routerConfig?: RouterConfig | (() => RouterConfig)): ModuleWithProviders<RouterModule> {
		return {
			ngModule: RouterModule,
			providers: [
				{ provide: LocationStrategy, useClass: PathLocationStrategy },
				{
					provide: HistoryService,
					useFactory: _historyService,
					deps: [PlatformService, Location, WindowRef, CacheFactory, RouteService],
				},
				{ provide: RouteReuseStrategy, useClass: ObgRouteReuseStrategy },
				{ provide: ROUTER_CONFIG, useFactory: _routerConfigFactory, deps: [_ROUTER_CONFIG] },
				{ provide: _ROUTER_CONFIG, useValue: routerConfig },
				{
					provide: APP_INITIALIZER,
					multi: true,
					useFactory: _initializeRouterFactory,
					deps: [Injector, PlatformService, ROUTER_CONFIG],
				},
				LOCALIZE_ROUTES_PROVIDER,
				REMOVE_DISABLED_ROUTES_PROVIDER,
			],
		};
	}
}

/**
 * @internal
 */
export function _historyService(
	platformService: PlatformService,
	location: Location,
	windowRef: WindowRef,
	cache: CacheFactory,
	routeService: RouteService
): any {
	return platformService.isBrowser
		? new AgressiveHistoryService(windowRef, routeService, cache)
		: new HistoryService(location);
}

/**
 * @internal
 */
export function _routerConfigFactory(routerConfig: RouterConfig | (() => RouterConfig)) {
	return isFunction(routerConfig) ? routerConfig() : routerConfig;
}

/**
 * @internal
 */
export function _initializeRouterFactory(injector: Injector, platformService: PlatformService, config: RouterConfig) {
	return _initializeRouter.bind(null, injector, platformService, config);
}

/**
 * @internal
 */
export function _initializeRouter(injector: Injector, platformService: PlatformService, config: RouterConfig) {
	if (platformService.isBrowser && config.handleLinksGlobally !== false) {
		injector.get(LinkHandlerService).initialize();
	}

	injector.get(LazyLoadingService).initialize();
	injector.get(RouteService).initialize();
}
